import React from 'react';

import { UploaderFile } from '../../common/Uploader';
import { CashOnDeliveryPaymentType, ClaimRequirements, ConsumerActionStatus } from '../../../api/types';
import { ClaimShopSettings } from '../ClaimsShopSettingsContext';
import { ClaimStepContainerProps } from './common/ClaimStepContainer';
import { SearchShipmentResult } from '../../claims-center/submit-claims/useSearchShipment';

export type MissingItemDescription = {
  id: string;
  amount: number;
  itemName: string;
};

export type DamageDescription = MissingItemDescription & {
  damageDescription: string;
};

export enum ClaimFileType {
  INVOICE_DOCUMENT = 'invoice_document',
  PICTURE_OF_DAMAGE = 'picture_of_damage',
  DECLARATION_OF_RECIPIENT = 'declaration_of_recipient',
  TRACKING_SCREENSHOT = 'tracking_screenshot',
  CUSTOMER_ACTION = 'customer_action_attachment',
  PICTURE_OF_MISSING_ITEM = 'picture_of_missing_item',
  NOTE_OF_LIABILITY = 'note_of_liability',
  REGRESS_INVOICE = 'regress_invoice',
  WAREHOUSE_DELIVERY_STATEMENT = 'warehouse_delivery_statement',
  DEADLINE_SUPPORTING_DOCUMENT = 'deadline_supporting_document',
  PROOF_OF_DELIVERY = 'proof_of_delivery',
  CARRIER_PAYMENT_CONFIRMATION = 'carrier_payment_confirmation',
  PROOF_OF_CONSUMER_PAYMENT = 'proof_of_consumer_payment',
  CARRIER_DAMAGE_REPORT = 'carrier_damage_report',
  DATA_PROCESSING_CONSENT = 'data_processing_consent',
  OTHER = 'other',
}

export enum ClaimType {
  INVESTIGATION = 'investigation',
  MISSING_ITEM = 'missing_item',
  PARTIAL_DAMAGE = 'partial_damage',
  WHOLE_DAMAGE = 'whole_damage',
  CASH_ON_DELIVERY = 'cash_on_delivery',
}

export enum ClaimStatus {
  ACCEPTED = 'accepted',
  ACTION_REQUIRED = 'action_required',
  CREDIT_NOTE_PENDING = 'credit_note_pending',
  DECLINED = 'declined',
  DRAFT = 'draft',
  IN_PROGRESS = 'in_progress',
  SUBMITTED = 'submitted',
  RESUBMITTED = 'resubmitted',
  SUCCESSFUL = 'successful',
  ESCALATING = 'escalating',
  PAYMENT_OVERDUE = 'payment_overdue',
  INCORRECT_PAYMENT = 'incorrect_payment',
  PAID = 'paid',
}

export type DeadlineEventKind =
  | 'first_hub_scan'
  | 'delivered'
  | 'postal_return_delivery'
  | 'postal_return'
  | 'warehouse_delivery_date'
  | 'shipment_creation'
  | 'last_event';

export type DeadlineOutcome = 'early' | 'past' | 'within';

export type Deadline = {
  daysLeftAfterEvent: number;
  eventDate: string;
  eventKind: DeadlineEventKind;
  firstSubmissionDate: string | null;
  lastSubmissionDate: string | null;
  maximumDaysAfterEvent: number;
  minimumDaysAfterEvent: number | null;
  outcome: DeadlineOutcome;
  useBusinessDays: boolean;
  useCarrierDeadlines: boolean;
  useWarehouseDelivery: boolean;
};

export type ClaimableWarning = 'mailbox_delivery_no_liability' | 'no_required_tracking_data';

export type SubmissionInfoForClaimType = {
  block: string | null;
  deadline: Deadline | null;
  warning: ClaimableWarning | null;
};

export type ClaimSubmissionInfo = {
  [key in ClaimType]: SubmissionInfoForClaimType;
};

export type OrderItem = {
  id: string; // some unique id to for validation
  orderItemId?: number; // id when the item is already saved in the backend
  unitPrice: string | number | null;
  quantity: string | number | null;
  description: string | null;
  taxRate?: number | string | null;
};

export enum SubmitClaimEntryPoint {
  CLAIMS_CENTER = 'claims-center',
  PARCEL_FINDER = 'parcel-finder',
}

export enum ShipmentType {
  OUTBOUND = 'outbound',
  RETURN = 'return',
  UNDELIVERABLE_OR_POSTAL_RETURN = 'undeliverable_or_postal_return',
}

export type ClaimContext = {
  draftId?: number;
  entryPoint: SubmitClaimEntryPoint;
  /** @deprecated in new flow, use `deliveryDate` and `postalDeliveryDate` instead */
  isDelivered?: boolean;
  type?: ClaimType;

  trackingCode?: string;
  recipientName?: string | null;
  recipientAddress?: string | null;
  recipientCity?: string | null;
  recipientZipCode?: string | null;
  recipientCountryCode?: string | null;
  recipientEmail?: string | null;
  recipientPhoneNumber?: string | null;

  weight?: number | string | null;

  carrierCode?: string;
  carrierCountryCode?: string;

  isReturnShipment?: boolean | null;
  shopOrderId?: string | null;
  orderId?: number | null;
  shopClaimId?: string | null;
  netValue?: number | string | null;
  netValueCurrency?: string;
  cashOnDeliveryPaymentType?: CashOnDeliveryPaymentType | null;

  firstHubScanDate?: Date | null;
  ssHubScanDate?: Date | null;
  warehouseDeliveryDate?: Date | null;
  shipmentCreationDate?: Date | null;
  deliveryDate?: Date | null;
  postalReturnDeliveryDate?: Date | null;
  deliveryAttemptDate?: Date | null;
  deliveredToMailboxDate?: Date | null;
  postalReturnDate?: Date | null;
  outsideOfDeadlineReason?: string | null;

  partialDamageInfo: DamageDescription[];
  missingItemInfo: MissingItemDescription[];

  orderItems: OrderItem[];
  isOrderItemsRequired?: boolean;
  isMultiShipmentOrder?: boolean | null;

  lmcCaseId?: string | null;
  customerComment?: string | null;
  damageInfo?: string | null; // rename to damageDescription to align with the backend
  wholeDamageInfo?: string;
  submissionInfo?: ClaimSubmissionInfo;
  shipmentType?: ShipmentType;
  shipmentId?: number | null;

  isTaxIncluded?: boolean;
  taxRate?: number | string | null;
  isOrderItemTaxRate?: boolean;

  availableDORLanguages?: string[];
  carrier?: ClaimShopCarrier;
  consumerActionStatus: ConsumerActionStatus | null;

  shouldForceShowEDORStep?: boolean;

  shopSettings: ClaimShopSettings;
  claimRequirements: ClaimRequirements | null;
} & ClaimContextFiles;

export type ClaimContextFiles = {
  declarationOfRecipient: UploaderFile[];
  trackingScreenshot: UploaderFile[];
  invoiceDocuments: UploaderFile[];
  picturesOfDamage: UploaderFile[];
  picturesOfMissingItem: UploaderFile[];
  otherDocuments: UploaderFile[];
  proofOfConsumerPayment: UploaderFile[];
  carrierDamageReport: UploaderFile[];
  deadlineSupportingDocuments: UploaderFile[];
  dataProcessingConsent: UploaderFile[];
};

export type ClaimShopCarrier = {
  id: number;
  name: string;
  code: string;
  countryName: string;
  countryCode: string;
  isNon7s: boolean;
  isPicturesOfMissingItemRequired: boolean;
  isDisplayCustomerPhoneNumberInputEnabled: boolean;
  isCarrierCaseIdAndCarrierDamageReportRequired: boolean;
  isWeightRequired: boolean;
  isDisplayCarrierCaseIdInputEnabled: boolean;
  isDataProcessingConsentRequired: boolean;
};

export type OnChangeClaimForm = (value: unknown, name: string) => void;
export type OnChangeClaimContext = (partial: Partial<ClaimContext>) => void;

export interface StepProps extends ClaimStepContainerProps {
  shopId: number;
  claim: ClaimContext;
  nonClaimableMessage?: string;
  as: React.ComponentType<ClaimStepContainerProps> | typeof React.Fragment;
  onChange: OnChangeClaimContext;
  onResetClaim: () => void;
  onResetShipmentSearch?: () => void;
  onLoadShipmentByTrackingId?: (trackingId: string) => Promise<SearchShipmentResult>;
  isManual: boolean;
  carriers: ClaimShopCarrier[];
}
